import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Clock,
  Globe,
  MessageSquare,
  Shield,
  FileText,
  History,
  Target,
  Link as LinkIcon,
  Eye,
  CheckCircle2,
  XCircle,
  BarChart3,
  Download,
  Briefcase
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';

interface ViolationComment {
  id: string;
  user: {
    name: string;
    avatar: string;
    role: string;
  };
  content: string;
  timestamp: string;
  attachments?: {
    name: string;
    type: string;
    url: string;
  }[];
}

interface ViolationEvidence {
  id: string;
  type: string;
  source: string;
  timestamp: string;
  description: string;
  data: any;
}

interface ViolationDetail {
  id: string;
  title: string;
  type: 'Domain' | 'Trademark' | 'Social';
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'active' | 'investigating' | 'mitigated' | 'resolved';
  detectedAt: string;
  lastUpdated: string;
  description: string;
  affectedAssets: string[];
  assignedTeam: string[];
  source: string;
  ttd: string;
  ttr: string;
  impactScore: number;
  evidence: ViolationEvidence[];
  comments: ViolationComment[];
  recommendations: string[];
  violatingUrls?: string[];
  screenshots?: string[];
}

const ViolationDetails = () => {
  const { id } = useParams();
  const [newComment, setNewComment] = useState('');

  // Mock data - in production, this would be fetched from an API
  const violation: ViolationDetail = {
    id: '1',
    title: 'Typosquatting Domain Detected',
    type: 'Domain',
    severity: 'high',
    status: 'investigating',
    detectedAt: '2025-02-09T10:30:00Z',
    lastUpdated: '2025-02-09T11:45:00Z',
    description: 'A potential typosquatting domain has been detected that closely resembles our brand name. The domain is actively hosting content that mimics our official website.',
    affectedAssets: [
      'Main Website',
      'Brand Name',
      'Customer Trust'
    ],
    assignedTeam: [
      'Brand Protection',
      'Legal Team',
      'Security Team'
    ],
    source: 'Domain Monitoring',
    ttd: '15 minutes',
    ttr: '2 hours',
    impactScore: 75,
    violatingUrls: [
      'https://example-typo.com',
      'https://brand-typo.net'
    ],
    evidence: [
      {
        id: 'ev-1',
        type: 'Domain Analysis',
        source: 'Domain Scanner',
        timestamp: '2025-02-09T10:30:00Z',
        description: 'Domain registration details and similarity analysis',
        data: {
          registrar: 'Example Registrar',
          registrationDate: '2025-02-08',
          similarityScore: '92%',
          nameservers: ['ns1.example.com', 'ns2.example.com']
        }
      },
      {
        id: 'ev-2',
        type: 'Content Match',
        source: 'Website Scanner',
        timestamp: '2025-02-09T10:35:00Z',
        description: 'Website content analysis showing brand impersonation',
        data: {
          contentSimilarity: '85%',
          brandMentions: 12,
          logoDetected: true
        }
      }
    ],
    comments: [
      {
        id: '1',
        user: {
          name: 'Sarah Chen',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sarah',
          role: 'Brand Protection Analyst'
        },
        content: 'Initial analysis confirms this is a sophisticated typosquatting attempt. The domain was registered yesterday and is using our branding assets.',
        timestamp: '2025-02-09T10:45:00Z'
      },
      {
        id: '2',
        user: {
          name: 'Mike Johnson',
          avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Mike',
          role: 'Legal Counsel'
        },
        content: 'I\'ve initiated the takedown process with our external counsel. We should have this resolved within 48 hours.',
        timestamp: '2025-02-09T11:00:00Z',
        attachments: [{
          name: 'takedown_notice.pdf',
          type: 'application/pdf',
          url: '#'
        }]
      }
    ],
    recommendations: [
      'Issue DMCA takedown notice',
      'Monitor for additional typosquatting domains',
      'Update brand protection rules',
      'Consider registering similar domains preventively'
    ]
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-500 bg-red-500/10';
      case 'high': return 'text-orange-500 bg-orange-500/10';
      case 'medium': return 'text-yellow-500 bg-yellow-500/10';
      case 'low': return 'text-green-500 bg-green-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-red-500 bg-red-500/10';
      case 'investigating': return 'text-blue-500 bg-blue-500/10';
      case 'mitigated': return 'text-orange-500 bg-orange-500/10';
      case 'resolved': return 'text-green-500 bg-green-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <AlertTriangle className="h-8 w-8 text-orange-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Violation Details</h1>
            <p className="text-sm text-gray-400">#{violation.id}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline">
            <History className="h-4 w-4 mr-2" />
            View Timeline
          </Button>
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Button>
            <Shield className="h-4 w-4 mr-2" />
            Take Action
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Key Information */}
        <div className="col-span-2 space-y-6">
          {/* Status Card */}
          <div className="bg-[#0F1629] rounded-xl p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white mb-2">{violation.title}</h2>
                <div className="flex items-center space-x-2">
                  <Badge className={getSeverityColor(violation.severity)}>
                    {violation.severity.toUpperCase()}
                  </Badge>
                  <Badge className={getStatusColor(violation.status)}>
                    {violation.status.toUpperCase()}
                  </Badge>
                  <Badge variant="outline">{violation.type}</Badge>
                </div>
              </div>
              <div className="text-sm text-gray-400">
                <div>Detected: {new Date(violation.detectedAt).toLocaleString()}</div>
                <div>Updated: {new Date(violation.lastUpdated).toLocaleString()}</div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-400 mb-1">Description</div>
                <p className="text-sm text-gray-300">{violation.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-400 mb-1">Affected Assets</div>
                  <div className="space-y-1">
                    {violation.affectedAssets.map((asset, index) => (
                      <div key={index} className="text-sm text-gray-300 flex items-center">
                        <Target className="h-4 w-4 mr-2 text-blue-400" />
                        {asset}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Assigned Team</div>
                  <div className="space-y-1">
                    {violation.assignedTeam.map((team, index) => (
                      <div key={index} className="text-sm text-gray-300">
                        {team}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {violation.violatingUrls && (
                <div>
                  <div className="text-sm text-gray-400 mb-2">Violating URLs</div>
                  <div className="space-y-2">
                    {violation.violatingUrls.map((url, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-800/30 rounded-lg p-3">
                        <div className="flex items-center space-x-2">
                          <LinkIcon className="h-4 w-4 text-blue-400" />
                          <span className="text-sm text-gray-300">{url}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <FileText className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Evidence */}
          <div className="bg-[#0F1629] rounded-xl p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Evidence
            </h2>

            <div className="space-y-4">
              {violation.evidence.map((item) => (
                <div
                  key={item.id}
                  className="bg-gray-800/30 rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Globe className="h-4 w-4 text-blue-400" />
                      <span className="text-sm font-medium text-white">{item.type}</span>
                    </div>
                    <Badge>{item.source}</Badge>
                  </div>
                  <div className="text-sm text-gray-400">{item.description}</div>
                  <div className="bg-gray-900 p-3 rounded-md">
                    <pre className="text-xs text-gray-300 overflow-x-auto">
                      {JSON.stringify(item.data, null, 2)}
                    </pre>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-[#0F1629] rounded-xl p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white flex items-center">
              <CheckCircle2 className="h-5 w-5 mr-2" />
              Recommendations
            </h2>

            <div className="space-y-3">
              {violation.recommendations.map((recommendation, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 bg-gray-800/30 rounded-lg p-3"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <CheckCircle2 className="h-5 w-5 text-blue-400" />
                  </div>
                  <span className="text-sm text-gray-300">{recommendation}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Comments Section */}
          <div className="bg-[#0F1629] rounded-xl p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white flex items-center justify-between">
              <div className="flex items-center">
                <MessageSquare className="h-5 w-5 mr-2" />
                Discussion
              </div>
              <span className="text-sm text-gray-400">{violation.comments?.length || 0} comments</span>
            </h2>

            {/* New Comment Input */}
            <div className="flex items-start space-x-4">
              <img
                src="https://api.dicebear.com/7.x/avataaars/svg?seed=techni"
                alt="Current User"
                className="w-10 h-10 rounded-full"
              />
              <div className="flex-1">
                <textarea
                  className="w-full px-3 py-2 text-sm bg-gray-800/50 border border-gray-700/50 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                  placeholder="Add a comment..."
                  rows={3}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
                <div className="mt-2 flex justify-end">
                  <Button size="sm">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Comment
                  </Button>
                </div>
              </div>
            </div>

            {/* Comments List */}
            <div className="space-y-6">
              {violation.comments?.map((comment) => (
                <div key={comment.id} className="flex space-x-4">
                  <img
                    src={comment.user.avatar}
                    alt={comment.user.name}
                    className="w-10 h-10 rounded-full"
                  />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-white">
                          {comment.user.name}
                        </span>
                        <span className="text-xs text-gray-400">
                          {comment.user.role}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(comment.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-300">{comment.content}</p>
                    {comment.attachments && (
                      <div className="flex items-center space-x-2">
                        {comment.attachments.map((attachment, index) => (
                          <a
                            key={index}
                            href={attachment.url}
                            className="inline-flex items-center px-3 py-1 rounded-full bg-gray-800/50 text-xs text-gray-300 hover:bg-gray-700/50 transition-colors"
                          >
                            <FileText className="h-3 w-3 mr-1" />
                            {attachment.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Analysis & Metrics */}
        <div className="space-y-6">
          {/* Response Metrics */}
          <div className="bg-[#0F1629] rounded-xl p-6 space-y-6">
            <h2 className="text-lg font-semibold text-white flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Impact Metrics
            </h2>

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Time to Detect</span>
                  <span className="text-sm text-white">{violation.ttd}</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-green-500 rounded-full" style={{ width: '85%' }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Time to Respond</span>
                  <span className="text-sm text-white">{violation.ttr}</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: '60%' }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Impact Score</span>
                  <span className="text-sm text-white">{violation.impactScore}%</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-orange-500 rounded-full" style={{ width: `${violation.impactScore}%` }} />
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-[#0F1629] rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white mb-2">Quick Actions</h2>
            <div className="space-y-2">
              <Button className="w-full justify-start" variant="outline">
                <Shield className="h-4 w-4 mr-2" />
                Issue Takedown Notice
              </Button>
              <Button className="w-full justify-start" variant="outline">
                <Globe className="h-4 w-4 mr-2" />
                Block Domain
              </Button>
              <Button className="w-full justify-start" variant="outline">
                <FileText className="h-4 w-4 mr-2" />
                Generate Report
              </Button>
              <Button className="w-full justify-start" variant="outline">
                <AlertTriangle className="h-4 w-4 mr-2" />
                Escalate to Legal
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViolationDetails;
